import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { AuthConnector } from '../types/user';

interface ConnectorContextValue {
    activeConnector: AuthConnector;
    connectors: AuthConnector[];
}

const ConnectorContext = createContext<ConnectorContextValue | null>(null);

export function ConnectorProvider({
    activeConnector,
    connectors,
    children,
}: {
    activeConnector: AuthConnector;
    connectors: AuthConnector[];
    children: ReactNode;
}) {
    const value = useMemo(() => ({ activeConnector, connectors }), [activeConnector, connectors]);
    return <ConnectorContext.Provider value={value}>{children}</ConnectorContext.Provider>;
}

export function useActiveConnector(): AuthConnector {
    const ctx = useContext(ConnectorContext);
    if (!ctx) {
        throw new Error('useActiveConnector doit être appelé à l\'intérieur d\'un ConnectorProvider');
    }
    return ctx.activeConnector;
}

export function useConnectors(): AuthConnector[] {
    const ctx = useContext(ConnectorContext);
    if (!ctx) {
        throw new Error('useConnectors doit être appelé à l\'intérieur d\'un ConnectorProvider');
    }
    return ctx.connectors;
}

/**
 * Retourne la liste des connectors autorisés pour l'utilisateur courant en lisant
 * le payload /api/auth/login stocké dans localStorage. Utilisable hors ConnectorProvider
 * (ex: AdminLayout, qui est parent des routes /:connectorSlug/*).
 */
export function readAuthConnectors(): AuthConnector[] {
    try {
        const raw = localStorage.getItem('auth_user');
        if (!raw) return [];
        const user = JSON.parse(raw) as { connectors?: AuthConnector[] };
        return Array.isArray(user.connectors) ? user.connectors : [];
    } catch {
        return [];
    }
}
